import type { ApiResponse } from "../types";
import i18n, { translateServerMessage } from "../i18n";

const BASE_URL = import.meta.env.VITE_API_URL ||
  (window.location.hostname.includes("bux.codes") ? "https://api.bux.codes/api" : "/api");

const TIMEOUT_MS = 10000;

// A 401 means the CN token expired or was revoked mid-session. Announce it so
// the auth provider can clear the dead session and route back to login instead
// of every page showing a generic error. (Login itself uses raw fetch, so this
// only fires on authenticated pages.)
function notifyUnauthorized(response: Response): void {
  if (response.status === 401) {
    window.dispatchEvent(new Event("auth:unauthorized"));
  }
}

async function parseErrorResponse(response: Response): Promise<string> {
  try {
    const body = await response.json() as { error?: string; message?: string };
    if (response.status === 403 && body.message?.includes("Student access is locked")) {
      window.dispatchEvent(new Event("auth:locked"));
    }
    // Known backend messages are localized via the catalog's "server" section.
    const serverMessage = body.message || body.error;
    return serverMessage
      ? translateServerMessage(serverMessage)
      : `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

function withTimeout(signal?: AbortSignal): { signal: AbortSignal; clear: () => void } {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), TIMEOUT_MS);
  if (signal) {
    signal.addEventListener("abort", () => controller.abort());
  }
  return { signal: controller.signal, clear: () => clearTimeout(id) };
}

// Student endpoints (profile, ledger, shop purchases, leaderboard) require the Code
// Ninjas token captured at login. Explicit extraHeaders still win.
function cnAuthHeaders(): Record<string, string> {
  const token = sessionStorage.getItem("cn_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function get<T>(
  url: string,
  extraHeaders?: Record<string, string>,
): Promise<ApiResponse<T>> {
  const { signal, clear } = withTimeout();
  try {
    const response = await fetch(`${BASE_URL}${url}`, {
      headers: { ...cnAuthHeaders(), ...extraHeaders },
      signal,
    });
    clear();
    if (!response.ok) {
      notifyUnauthorized(response);
      return { error: await parseErrorResponse(response) };
    }
    const data = await response.json();
    return { data };
  } catch (error) {
    clear();
    if (error instanceof DOMException && error.name === "AbortError") {
      return { error: i18n.t("errors.timeout") };
    }
    return { error: i18n.t("errors.network") };
  }
}

export async function post<T>(
  url: string,
  body: unknown,
  extraHeaders?: Record<string, string>,
): Promise<ApiResponse<T>> {
  const { signal, clear } = withTimeout();
  try {
    const response = await fetch(`${BASE_URL}${url}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...cnAuthHeaders(),
        ...extraHeaders,
      },
      body: JSON.stringify(body),
      signal,
    });
    clear();
    if (!response.ok) {
      notifyUnauthorized(response);
      return { error: await parseErrorResponse(response) };
    }
    const data = await response.json();
    return { data };
  } catch (error) {
    clear();
    if (error instanceof DOMException && error.name === "AbortError") {
      return { error: i18n.t("errors.timeout") };
    }
    return { error: i18n.t("errors.network") };
  }
}

